import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Check, 
  Shield, 
  FileText, 
  Home, 
  AlertTriangle,
  UserCheck,
  Scale,
  HelpCircle,
  Mail
} from 'lucide-react';

const TerminosCondiciones = () => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState('introduccion');

  // Secciones de los términos y condiciones
  const sections = [
    { 
      id: 'introduccion', 
      title: 'Introducción',
      icon: FileText
    },
    { 
      id: 'definiciones', 
      title: 'Definiciones',
      icon: HelpCircle 
    },
    { 
      id: 'registro', 
      title: 'Registro y Cuentas',
      icon: UserCheck 
    },
    { 
      id: 'publicacion', 
      title: 'Publicación de Inmuebles',
      icon: Home 
    },
    { 
      id: 'responsabilidad', 
      title: 'Limitación de Responsabilidad',
      icon: Shield 
    },
    { 
      id: 'obligaciones', 
      title: 'Obligaciones de Usuarios',
      icon: Check 
    },
    { 
      id: 'propiedad', 
      title: 'Propiedad Intelectual',
      icon: FileText 
    },
    { 
      id: 'suspension', 
      title: 'Suspensión de Cuentas',
      icon: AlertTriangle 
    },
    { 
      id: 'legislacion', 
      title: 'Legislación Aplicable',
      icon: Scale 
    }
  ];

  const handleSelectSection = (sectionId) => {
    setActiveSectionId(sectionId);
    // Scroll to the section
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBackToRegister = () => {
    // En un entorno real, esto usaría el router para volver a la página anterior
    // Pero para este demo solo mostramos un alert
    alert('Regresar a registro');
    // window.history.back();
  };

  const handleContinue = () => {
    if (acceptedTerms) {
      alert('Términos aceptados - Continuando con el registro');
      // Aquí redirigirías o actualizarías el estado
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-pink-50 py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-100 to-transparent opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-t from-pink-100 to-transparent opacity-40"></div>
        
        {/* Decorative circles */}
        <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-blue-200 opacity-20 blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-purple-200 opacity-20 blur-xl"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-emerald-200 opacity-20 blur-xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button 
            onClick={handleBackToRegister}
            className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            <span>Volver al registro</span>
          </button>
          
          <div className="flex items-center gap-2">
            <img 
              src="/InKairos.ico" 
              alt="InKayros Logo" 
              className="w-9 h-9"
            />
            <h1 className="text-2xl font-bold text-gray-800">InKayros.com</h1>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          {/* Title bar */}
          <div className="bg-blue-600 text-white p-6">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Términos y Condiciones de Uso</h2>
            </div>
            <p className="text-blue-100 mt-2">Última actualización: Marzo 2025</p>
          </div>

          <div className="flex flex-col lg:flex-row">
            {/* Sidebar navigation */}
            <div className="lg:w-72 border-r border-gray-200 bg-gray-50">
              <div className="p-4">
                <h3 className="font-medium text-gray-700 mb-3">Contenido</h3>
                <nav className="space-y-1">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => handleSelectSection(section.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
                          activeSectionId === section.id
                            ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-blue-600'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={18} className={activeSectionId === section.id ? 'text-blue-600' : 'text-gray-400'} />
                        <span>{section.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 p-6 lg:p-8 max-w-4xl overflow-y-auto">
              <div className="prose max-w-full">
                <section id="introduccion">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FileText className="h-6 w-6 text-blue-600" />
                    Introducción
                  </h2>
                  <p className="text-gray-700">
                    Bienvenido a InKayros.com (en adelante, "InKayros", "la plataforma", "el sitio web", "nosotros" o "nuestro"). 
                    InKayros es una plataforma digital que facilita la conexión entre propietarios, vendedores, agentes inmobiliarios, 
                    inmobiliarias, constructoras y potenciales compradores o arrendatarios de bienes inmuebles.
                  </p>
                  <p className="text-gray-700 mt-4">
                    Estos Términos y Condiciones regulan el acceso y uso de la plataforma InKayros.com, sus contenidos y servicios que 
                    ponemos a disposición de los usuarios. Al registrarse, acceder y utilizar nuestros servicios, usted acepta estos 
                    términos y condiciones en su totalidad, así como nuestra Política de Privacidad.
                  </p>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="definiciones">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <HelpCircle className="h-6 w-6 text-blue-600" />
                    Definiciones
                  </h2>
                  <p className="text-gray-700">Para los fines de estos Términos y Condiciones:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                    <li>
                      <strong>Usuario</strong>: toda persona natural o jurídica que acceda, navegue o utilice los servicios ofrecidos por InKayros.
                    </li>
                    <li>
                      <strong>Anunciante</strong>: usuario que publica propiedades para venta o alquiler en la plataforma.
                    </li>
                    <li>
                      <strong>Propiedad</strong>: bien inmueble (casa, departamento, terreno, local comercial, etc.) publicado en InKayros.
                    </li>
                    <li>
                      <strong>Contenido</strong>: información, textos, imágenes, videos y datos que los usuarios publican en la plataforma.
                    </li>
                    <li>
                      <strong>Servicios</strong>: conjunto de funcionalidades que ofrece InKayros para conectar a los usuarios.
                    </li>
                  </ul>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="registro">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <UserCheck className="h-6 w-6 text-blue-600" />
                    Registro y Cuentas de Usuario
                  </h2>
                  <h3 className="text-lg font-semibold text-gray-700 mt-4">3.1. Requisitos para registrarse</h3>
                  <p className="text-gray-700 mt-2">
                    Para utilizar determinados servicios de InKayros, deberá registrarse y crear una cuenta como usuario. 
                    Al hacerlo, usted declara y garantiza que:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Es mayor de 18 años o cuenta con la autorización de sus padres o tutores legales.</li>
                    <li>Tiene capacidad legal para celebrar contratos vinculantes.</li>
                    <li>La información proporcionada es veraz, completa, exacta y actualizada.</li>
                    <li>Mantendrá la confidencialidad de sus datos de acceso.</li>
                    <li>Será el único responsable de todas las actividades realizadas bajo su cuenta.</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">3.2. Tipos de cuentas</h3>
                  <p className="text-gray-700 mt-2">
                    InKayros ofrece diferentes tipos de cuentas según el perfil del usuario:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Cliente</li>
                    <li>Vendedor</li>
                    <li>Inmobiliaria</li>
                    <li>Constructora</li>
                  </ul>
                  <p className="text-gray-700 mt-2">
                    Cada tipo de cuenta tiene funcionalidades y responsabilidades específicas detalladas durante el proceso de registro.
                  </p>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="publicacion">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Home className="h-6 w-6 text-blue-600" />
                    Publicación de Inmuebles
                  </h2>
                  <h3 className="text-lg font-semibold text-gray-700 mt-4">4.1. Responsabilidad sobre los inmuebles publicados</h3>
                  <p className="text-gray-700 mt-2">
                    Al publicar una propiedad en InKayros, el anunciante declara y garantiza expresamente que:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Es el propietario legítimo del inmueble o cuenta con la autorización expresa del propietario para publicarlo y comercializarlo.</li>
                    <li>Posee todos los derechos, licencias, consentimientos y autorizaciones necesarios conforme a la legislación peruana e internacional aplicable.</li>
                    <li>La información proporcionada sobre el inmueble es veraz, completa y exacta, incluyendo sus características, condiciones, precio y disponibilidad.</li>
                    <li>Las imágenes, videos y documentos compartidos corresponden efectivamente al inmueble anunciado.</li>
                    <li>El inmueble cumple con la normativa urbanística, fiscal y legal correspondiente.</li>
                    <li>Asume total responsabilidad por el contenido publicado y las interacciones derivadas de su anuncio.</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">4.2. Prohibiciones</h3>
                  <p className="text-gray-700 mt-2">
                    Está estrictamente prohibido publicar inmuebles que:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>No existan o no estén disponibles realmente para su venta o alquiler.</li>
                    <li>Sean objeto de litigio o tengan problemas legales pendientes.</li>
                    <li>No cuenten con la documentación legal correspondiente.</li>
                    <li>Provengan de actividades ilícitas o fraudulentas.</li>
                    <li>Incumplan la normativa urbanística o ambiental vigente.</li>
                  </ul>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="responsabilidad">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Shield className="h-6 w-6 text-blue-600" />
                    Limitación de Responsabilidad
                  </h2>
                  <h3 className="text-lg font-semibold text-gray-700 mt-4">5.1. InKayros como intermediario</h3>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mt-2">
                    <p className="text-blue-700">
                      InKayros actúa exclusivamente como un medio digital que facilita la conexión entre anunciantes y potenciales compradores 
                      o arrendatarios. En consecuencia:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-1 text-blue-700">
                      <li>No somos propietarios de los inmuebles publicados.</li>
                      <li>No participamos en las transacciones entre usuarios.</li>
                      <li>No validamos ni verificamos la titularidad o condiciones legales de las propiedades anunciadas.</li>
                      <li>No garantizamos la exactitud de la información proporcionada por los anunciantes.</li>
                      <li>No somos parte en los contratos que puedan generarse entre usuarios.</li>
                    </ul>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">5.2. Deslinde de responsabilidad</h3>
                  <p className="text-gray-700 mt-2">
                    InKayros se deslinda expresamente de cualquier responsabilidad derivada de:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>La veracidad y exactitud de la información proporcionada por los usuarios.</li>
                    <li>Los problemas o controversias surgidos en las negociaciones o transacciones entre usuarios.</li>
                    <li>El incumplimiento de obligaciones contractuales entre las partes.</li>
                    <li>Las condiciones reales de los inmuebles una vez inspeccionados físicamente.</li>
                    <li>Cualquier daño o perjuicio resultante del uso incorrecto de la plataforma.</li>
                    <li>Posibles estafas, fraudes o conductas ilícitas cometidas por los usuarios.</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">5.3. Compromiso con los usuarios afectados</h3>
                  <p className="text-gray-700 mt-2">
                    No obstante lo anterior, InKayros se compromete a:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Tomar medidas razonables para mantener la seguridad de la plataforma.</li>
                    <li>Proporcionar canales de comunicación para reportar conductas indebidas.</li>
                    <li>Colaborar con las autoridades competentes en casos de fraude o actividades ilícitas.</li>
                    <li>Brindar asistencia a los usuarios afectados por infracciones a estos términos.</li>
                    <li>Suspender o eliminar cuentas que incumplan nuestras políticas.</li>
                  </ul>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="obligaciones">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Check className="h-6 w-6 text-blue-600" />
                    Obligaciones de los Usuarios
                  </h2>

                  <h3 className="text-lg font-semibold text-gray-700 mt-4">6.1. Obligaciones generales</h3>
                  <p className="text-gray-700 mt-2">
                    Todos los usuarios de InKayros se comprometen a:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Cumplir con estos Términos y Condiciones y demás políticas de la plataforma.</li>
                    <li>Proporcionar información veraz, actualizada y completa.</li>
                    <li>Utilizar la plataforma de manera leal y de buena fe.</li>
                    <li>Respetar los derechos de terceros.</li>
                    <li>No realizar conductas contrarias a la ley, moral, buenas costumbres u orden público.</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">6.2. Obligaciones específicas para anunciantes</h3>
                  <p className="text-gray-700 mt-2">
                    Los anunciantes se comprometen adicionalmente a:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Publicar únicamente inmuebles sobre los que tengan legítimo derecho.</li>
                    <li>Proporcionar información exacta y veraz sobre los inmuebles.</li>
                    <li>Mantener actualizada la información sobre disponibilidad y condiciones.</li>
                    <li>Responder a las consultas de los interesados en un plazo razonable.</li>
                    <li>Gestionar las transacciones con profesionalidad y buena fe.</li>
                    <li>Cumplir con las obligaciones fiscales y tributarias correspondientes.</li>
                  </ul>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="propiedad">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FileText className="h-6 w-6 text-blue-600" />
                    Propiedad Intelectual
                  </h2>
                  <h3 className="text-lg font-semibold text-gray-700 mt-4">7.1. Contenido de la plataforma</h3>
                  <p className="text-gray-700 mt-2">
                    El contenido propio de InKayros, incluyendo pero no limitado a su diseño, estructura, selección, coordinación, 
                    expresión, logos, marcas y software, está protegido por derechos de propiedad intelectual e industrial.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">7.2. Contenido publicado por usuarios</h3>
                  <p className="text-gray-700 mt-2">
                    Al publicar contenido en InKayros, el usuario mantiene la titularidad de sus derechos pero otorga a InKayros una 
                    licencia mundial, no exclusiva, gratuita, transferible y sublicenciable para usar, reproducir, distribuir, 
                    preparar obras derivadas, mostrar y comunicar dicho contenido en relación con los servicios de InKayros.
                  </p>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="suspension">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-6 w-6 text-blue-600" />
                    Suspensión y Cancelación de Cuentas
                  </h2>
                  <p className="text-gray-700">
                    InKayros se reserva el derecho de suspender temporalmente o cancelar definitivamente las cuentas de los usuarios que:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                    <li>Proporcionen información falsa o fraudulenta.</li>
                    <li>Realicen prácticas de suplantación de identidad.</li>
                    <li>Publiquen propiedades sobre las que no tengan legítimos derechos.</li>
                    <li>Participen en estafas o actividades fraudulentas.</li>
                    <li>Incumplan estos Términos y Condiciones o cualquier ley aplicable.</li>
                    <li>Utilicen la plataforma para fines distintos a los previstos.</li>
                    <li>Afecten negativamente la experiencia de otros usuarios.</li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    La suspensión o cancelación de una cuenta implicará la eliminación de las publicaciones asociadas a la misma.
                  </p>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="legislacion">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Scale className="h-6 w-6 text-blue-600" />
                    Legislación Aplicable y Jurisdicción
                  </h2>
                  <h3 className="text-lg font-semibold text-gray-700 mt-4">9.1. Legislación aplicable</h3>
                  <p className="text-gray-700 mt-2">
                    Estos Términos y Condiciones se rigen por la legislación de la República del Perú, incluyendo pero no limitado a:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Código Civil Peruano.</li>
                    <li>Ley N° 29571, Código de Protección y Defensa del Consumidor.</li>
                    <li>Ley N° 29733, Ley de Protección de Datos Personales.</li>
                    <li>Ley N° 27291, Ley que modifica el Código Civil permitiendo la utilización de medios electrónicos.</li>
                    <li>Demás normas aplicables a servicios digitales, comercio electrónico y sector inmobiliario.</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">9.2. Resolución de controversias</h3>
                  <p className="text-gray-700 mt-2">
                    Cualquier controversia derivada de estos Términos y Condiciones será resuelta mediante negociación directa entre las partes. 
                    De no llegar a un acuerdo, las partes se someten a la jurisdicción de los Juzgados y Tribunales de Lima, Perú, 
                    renunciando a cualquier otro fuero que pudiera corresponderles.
                  </p>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section className="bg-gray-50 p-6 rounded-xl">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    Contacto
                  </h2>
                  <p className="text-gray-700">
                    Para cualquier consulta, sugerencia o reclamación relacionada con estos Términos y Condiciones, 
                    puede ponerse en contacto con nosotros a través de:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                    <li>Correo electrónico: <span className="text-blue-600">legal@inkayros.com</span></li>
                    <li>Formulario de contacto en nuestra plataforma</li>
                  </ul>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminosCondiciones;